import { useState } from 'react'
import { FiEdit2, FiSend, FiShare2, FiThumbsUp, FiTrash2 } from 'react-icons/fi'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import { WEBINAR_FAVOURITE_TYPE } from '@domain/features/user-favourites/user-favourites-feature'
import { createWebinarQuestionLikedNotification } from '@domain/features/user-notifications/user-notifications-feature'
import {
  deleteSubmittedWebinarQuestion,
  deleteExistingWebinar,
  submitWebinarQuestion,
  toggleSubmittedWebinarQuestionLike,
} from '@domain/features/webinars/webinars-feature'
import { CommentsOn } from '@domain/models/activity-comments'
import CommentsSection from '@presentation/components/comments/CommentsSection'
import NewsShareModal from '@presentation/components/news/NewsShareModal'
import FavouriteBookmarkButton from '@presentation/components/ui/FavouriteBookmarkButton'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import YouTubeEmbed from '@presentation/components/ui/YouTubeEmbed'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useScrollToTopOnRouteEnter } from '@presentation/hooks/useScrollToTopOnRouteEnter'
import { useSubmittedWebinarQuestions } from '@presentation/hooks/useSubmittedWebinarQuestions'
import { useUserFavourite } from '@presentation/hooks/useUserFavourite'
import { useWebinar } from '@presentation/hooks/useWebinar'
import {
  normalizeMarkdownForRendering,
  stripMarkdownToPlainText,
} from '@presentation/helpers/markdown'
import {
  extractYouTubeVideoId,
  formatWebinarDate,
  formatWebinarDateTime,
  isUpcomingWebinar,
} from '@presentation/helpers/webinar'
import SeoHead from '@presentation/seo/SeoHead'
import StructuredData from '@presentation/seo/StructuredData'
import { sanitizeForMeta, toAbsoluteUrl } from '@presentation/seo/seo-constants'
import { buildWebinarStructuredData } from './webinarStructuredData'

const QUESTION_TEXTAREA_CLASS = 'w-full rounded-xl border border-border-light bg-surface px-4 py-3 text-base font-semibold text-text-primary shadow-sm transition-colors placeholder:text-text-muted focus:border-blue! focus:outline-none! focus:ring-0! focus-visible:rounded-xl! focus-visible:border-blue! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0!'

const summaryMarkdownComponents: Components = {
  p: (props) => <p className="mt-4 first:mt-0" {...props} />,
  ul: (props) => <ul className="mt-4 list-disc space-y-1 pl-6" {...props} />,
  ol: (props) => <ol className="mt-4 list-decimal space-y-1 pl-6" {...props} />,
  li: (props) => <li className="text-text-primary" {...props} />,
  strong: (props) => <strong className="font-bold text-text-primary" {...props} />,
  a: ({ href, ...props }) => (
    <a
      {...props}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-semibold text-primary underline underline-offset-2 transition-colors hover:text-primary-light"
    />
  ),
}

function formatSubmittedOn(date: Date): string {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function WebinarDetailPage() {
  const location = useLocation()
  useScrollToTopOnRouteEnter({ scrollOnPop: true })
  const navigate = useNavigate()
  const screenPath = `${location.pathname}${location.search || ''}`
  const { webinarId } = useParams<{ webinarId: string }>()
  const { user } = useAuthUser()
  const { webinar, loading, error } = useWebinar(webinarId)
  const {
    isFavourited,
    loading: favouriteLoading,
    toggling: favouriteToggling,
    error: favouriteError,
    toggle,
  } = useUserFavourite(webinar?.uid, WEBINAR_FAVOURITE_TYPE)
  const shouldLoadSubmittedQuestions = webinar
    ? isUpcomingWebinar(webinar.webinarDate)
    : false
  const [question, setQuestion] = useState('')
  const [isSubmittingQuestion, setIsSubmittingQuestion] = useState(false)
  const [questionError, setQuestionError] = useState<string | null>(null)
  const [questionSuccess, setQuestionSuccess] = useState<string | null>(null)
  const [questionActionError, setQuestionActionError] = useState<string | null>(null)
  const [activeLikeQuestionId, setActiveLikeQuestionId] = useState<string | null>(null)
  const [activeDeleteQuestionId, setActiveDeleteQuestionId] = useState<string | null>(null)
  const [adminActionError, setAdminActionError] = useState<string | null>(null)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const {
    questions: submittedQuestions,
    loading: submittedQuestionsLoading,
    error: submittedQuestionsError,
    reload: reloadSubmittedQuestions,
  } = useSubmittedWebinarQuestions(
    webinar?.uid,
    { enabled: shouldLoadSubmittedQuestions },
  )

  const openLoginModal = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', 'login')

    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const handleEdit = () => {
    if (!webinar?.uid) {
      return
    }
    navigate(`/webinars/${webinar.uid}/edit`)
  }

  const handleDelete = async () => {
    if (!webinar?.uid) {
      return
    }

    const confirmed = window.confirm('Are you sure you want to delete this webinar?')
    if (!confirmed) {
      return
    }

    setAdminActionError(null)
    try {
      await deleteExistingWebinar(webinar.uid)
      const backPath = isUpcomingWebinar(webinar.webinarDate)
        ? '/webinars/upcoming'
        : '/webinars/latest'
      navigate(backPath)
    } catch (deleteError) {
      setAdminActionError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Failed to delete webinar.',
      )
    }
  }

  const handleSubmitQuestion = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!webinar?.uid) {
      return
    }

    if (!user) {
      openLoginModal()
      return
    }

    setQuestionError(null)
    setQuestionSuccess(null)
    setIsSubmittingQuestion(true)

    try {
      await submitWebinarQuestion(webinar.uid, question)
      AnalyticsFeature.trackWebinarQuestionSubmit(
        screenPath,
        webinar.uid,
        Boolean(user),
      )
      reloadSubmittedQuestions()
      setQuestion('')
      setQuestionSuccess('Your question has been submitted.')
    } catch (submitError) {
      setQuestionError(
        submitError instanceof Error
          ? submitError.message
          : 'Failed to submit question.',
      )
    } finally {
      setIsSubmittingQuestion(false)
    }
  }

  const handleToggleQuestionLike = async (questionId: string, submittedByUid?: string) => {
    if (!webinar?.uid) {
      return
    }

    if (!user) {
      openLoginModal()
      return
    }

    setQuestionActionError(null)
    setActiveLikeQuestionId(questionId)
    try {
      const didLike = await toggleSubmittedWebinarQuestionLike(webinar.uid, questionId)
      if (didLike && user?.uid && submittedByUid) {
        void createWebinarQuestionLikedNotification({
          forUserId: submittedByUid,
          senderId: user.uid,
          senderSlug: user.profileSlug ?? undefined,
          webinarId: webinar.uid,
          questionId,
        })
      }
      reloadSubmittedQuestions()
    } catch (toggleLikeError) {
      setQuestionActionError(
        toggleLikeError instanceof Error
          ? toggleLikeError.message
          : 'Failed to update question like.',
      )
    } finally {
      setActiveLikeQuestionId(null)
    }
  }

  const handleDeleteSubmittedQuestion = async (questionId: string) => {
    if (!webinar?.uid || !user?.isAdmin) {
      return
    }

    const confirmed = window.confirm('Are you sure you want to delete this question?')
    if (!confirmed) {
      return
    }

    setQuestionActionError(null)
    setActiveDeleteQuestionId(questionId)
    try {
      await deleteSubmittedWebinarQuestion(webinar.uid, questionId)
      reloadSubmittedQuestions()
    } catch (deleteQuestionError) {
      setQuestionActionError(
        deleteQuestionError instanceof Error
          ? deleteQuestionError.message
          : 'Failed to delete submitted question.',
      )
    } finally {
      setActiveDeleteQuestionId(null)
    }
  }

  if (loading) {
    return (
      <>
        <SeoHead
          title="Loading webinar | Parkinson's Insights"
          canonicalPath={webinarId ? `/webinars/${webinarId}` : '/webinars/latest'}
        />
        <LoadingSpinner className="min-h-[50vh]" />
      </>
    )
  }

  if (!webinar) {
    return (
      <>
        <SeoHead
          title="Webinar not found | Parkinson's Insights"
          canonicalPath={webinarId ? `/webinars/${webinarId}` : '/webinars/latest'}
          noindex
        />
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
          <p className="text-lg font-semibold text-text-secondary">
            {error ?? 'Webinar not found'}
          </p>
          <Link
            to="/webinars"
            className="mt-4 text-sm font-semibold text-primary hover:text-primary-light"
          >
            &larr; Back to Webinars
          </Link>
        </div>
      </>
    )
  }

  const isUpcoming = isUpcomingWebinar(webinar.webinarDate)
  const videoId = extractYouTubeVideoId(webinar.youtubeUrl)
  const coverImageUrl = webinar.coverImageUrl?.trim()
  const normalizedSummaryMarkdown = normalizeMarkdownForRendering(webinar.summary ?? '')
  const webinarDateLabel = isUpcoming
    ? formatWebinarDateTime(webinar.webinarDate, webinar.webinarTime, webinar.timeZone)
    : formatWebinarDate(webinar.webinarDate)
  const webinarsBackPath = isUpcoming ? '/webinars/upcoming' : '/webinars/latest'
  const navigateBackToWebinars = () => {
    if (location.key !== 'default') {
      navigate(-1)
      return
    }

    navigate(webinarsBackPath, { replace: true })
  }
  const webinarDescription = sanitizeForMeta(
    stripMarkdownToPlainText(normalizedSummaryMarkdown || webinar.title),
    170,
  )
  const ogImageUrl = coverImageUrl || (videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : '')
  const canonicalPath = `/webinars/${webinar.uid}`
  const structuredData = buildWebinarStructuredData({
    webinar,
    canonicalPath,
    webinarDescription,
    ogImageUrl: ogImageUrl || undefined,
    isUpcoming,
  })
  const shareUrl = toAbsoluteUrl(canonicalPath)
  const shareImageUrl = ogImageUrl ? toAbsoluteUrl(ogImageUrl) : undefined

  return (
    <article className="bg-background">
      <SeoHead
        title={`${webinar.title} | Parkinson's Webinar`}
        description={webinarDescription}
        canonicalPath={canonicalPath}
        ogType="video.other"
        imageUrl={ogImageUrl}
      />
      <StructuredData data={structuredData} />
      <div className="mx-auto w-full max-w-5xl px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={navigateBackToWebinars}
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Webinars
          </button>

          <div className="ml-auto flex w-full items-center justify-end gap-2 sm:w-auto">
            {user?.isAdmin ? (
              <>
                <button
                  type="button"
                  onClick={handleEdit}
                  aria-label="Edit webinar"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-blue bg-surface text-blue transition-colors hover:bg-blue/10"
                >
                  <FiEdit2 className="h-5 w-5" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void handleDelete()
                  }}
                  aria-label="Delete webinar"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-red-500 bg-surface text-red-500 transition-colors hover:bg-red-500/10"
                >
                  <FiTrash2 className="h-5 w-5" aria-hidden />
                </button>
              </>
            ) : null}
            <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center">
              <FavouriteBookmarkButton
                isFavourited={isFavourited}
                onClick={() => {
                  void toggle(openLoginModal)
                }}
                disabled={favouriteLoading || favouriteToggling}
                className="h-11 shrink-0 gap-2 bg-background! px-4 text-blue! hover:bg-blue/10!"
                label={isFavourited ? 'Favorited' : 'Add To Favourites'}
                ariaLabel={isFavourited ? 'Remove webinar from favourites' : 'Add webinar to favourites'}
              />
              <button
                type="button"
                onClick={() => {
                  setIsShareModalOpen(true)
                }}
                aria-label="Share webinar"
                className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-blue bg-background px-4 text-blue transition-colors hover:bg-blue/10"
              >
                <FiShare2 className="h-5 w-5" aria-hidden />
                <span className="text-sm font-semibold">Share</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {coverImageUrl ? (
        <div className="mx-auto w-full max-w-5xl px-4 pt-4 sm:px-6 sm:pt-5 lg:px-8">
          <div className="overflow-hidden rounded-xl border border-border-light bg-border-light">
            <img
              src={coverImageUrl}
              alt={webinar.title}
              className="h-64 w-full object-cover sm:h-80"
            />
          </div>
        </div>
      ) : null}

      {videoId ? (
        <div className="mx-auto w-full max-w-5xl px-4 pt-4 sm:px-6 sm:pt-5 lg:px-8">
          <YouTubeEmbed
            videoId={videoId}
            title={webinar.title}
            className="shadow-none"
          />
        </div>
      ) : !isUpcoming ? (
        <div className="mx-auto w-full max-w-5xl px-4 pt-4 sm:px-6 sm:pt-5 lg:px-8">
          <div className="flex aspect-video items-center justify-center rounded-xl bg-border-light text-sm font-semibold text-text-muted">
            Video not available.
          </div>
        </div>
      ) : null}

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold tracking-tight text-text-primary sm:text-4xl">
          {webinar.title}
        </h1>

        <p className={`mt-3 font-bold text-primary ${isUpcoming ? 'text-base sm:text-lg' : 'text-sm'}`}>
          {webinarDateLabel}
        </p>

        {adminActionError ? (
          <p className="mt-2 text-sm text-text-secondary">{adminActionError}</p>
        ) : null}
        {favouriteError ? (
          <p className="mt-2 text-sm text-text-secondary">{favouriteError}</p>
        ) : null}

        {normalizedSummaryMarkdown ? (
          <div className="mt-6 text-text-primary leading-relaxed">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={summaryMarkdownComponents}
            >
              {normalizedSummaryMarkdown}
            </ReactMarkdown>
          </div>
        ) : null}

        {isUpcoming && webinar.registerUrl ? (
          <div className="mt-8 flex justify-center">
            <a
              href={webinar.registerUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                AnalyticsFeature.trackWebinarRegisterClick(
                  screenPath,
                  webinar.uid,
                  Boolean(user),
                )
              }}
              className="inline-flex rounded-xl bg-blue px-8 py-3 text-sm font-bold text-white no-underline transition-colors hover:bg-blue/90"
            >
              Register
            </a>
          </div>
        ) : null}

        {isUpcoming ? (
          <section className="mt-10 rounded-2xl border border-border-light bg-background p-4 sm:p-6">
            <h2 className="text-2xl font-bold text-text-primary">Submit a question</h2>
            <p className="mt-1 text-sm text-text-secondary">
              Share your question for this upcoming webinar. The most liked questions will be asked of the speaker during the webinar.
            </p>

            {!user ? (
              <div className="mt-4">
                <p className="text-sm text-text-secondary">
                  Please log in to submit a question.
                </p>
                <button
                  type="button"
                  onClick={openLoginModal}
                  className="mt-3 rounded-xl bg-primary px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-primary-light"
                >
                  Log In
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmitQuestion} className="mt-4">
                <div className="flex items-end gap-2">
                  <textarea
                    value={question}
                    onChange={(event) => setQuestion(event.target.value)}
                    rows={3}
                    placeholder="Write a question..."
                    className={QUESTION_TEXTAREA_CLASS}
                    required
                  />
                  <button
                    type="submit"
                    disabled={isSubmittingQuestion || question.trim().length === 0}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue text-white transition-colors hover:bg-blue/90 disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label="Submit question"
                  >
                    <FiSend className="h-4 w-4" />
                  </button>
                </div>
                {(questionError || questionSuccess) && (
                  <p className="mt-2 text-sm font-semibold text-text-secondary">
                    {questionError ?? questionSuccess}
                  </p>
                )}
              </form>
            )}

            <hr className="mt-6 border-border-light" />

            <div className="mt-5">
              <h3 className="text-lg font-bold text-text-primary">Submitted questions</h3>
              {!user ? (
                <p className="mt-1 text-sm text-text-secondary">
                  Log in to like questions.
                </p>
              ) : null}

              {(submittedQuestionsError || questionActionError) && (
                <p className="mt-3 text-sm font-semibold text-text-secondary">
                  {submittedQuestionsError ?? questionActionError}
                </p>
              )}

              {submittedQuestionsLoading ? (
                <p className="mt-4 text-base font-semibold text-text-secondary">
                  Loading submitted questions...
                </p>
              ) : submittedQuestions.length === 0 ? (
                <p className="mt-4 text-base font-semibold text-text-secondary">
                  No submitted questions yet.
                </p>
              ) : (
                <div className="mt-4 space-y-3">
                  {submittedQuestions.map((submittedQuestion) => {
                    const liked = submittedQuestion.likedByCurrentUser === true
                    const isLikePending = activeLikeQuestionId === submittedQuestion.uid
                    const isDeletePending = activeDeleteQuestionId === submittedQuestion.uid

                    return (
                      <article
                        key={submittedQuestion.uid}
                        className="rounded-xl border border-border-light bg-surface p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-base font-semibold text-text-primary sm:text-lg">
                              {submittedQuestion.question}
                            </p>
                            <p className="mt-2 text-sm text-text-secondary">
                              {submittedQuestion.submittedByName
                                || submittedQuestion.submittedByUid
                                || 'Unknown user'}
                              {' · '}
                              {formatSubmittedOn(submittedQuestion.submittedOn)}
                            </p>
                          </div>
                          {user?.isAdmin ? (
                            <button
                              type="button"
                              onClick={() => {
                                void handleDeleteSubmittedQuestion(submittedQuestion.uid)
                              }}
                              disabled={isDeletePending}
                              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-red-500 bg-surface text-red-500 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60"
                              aria-label="Delete submitted question"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          ) : null}
                        </div>

                        <div className="mt-4">
                          <button
                            type="button"
                            onClick={() => {
                              void handleToggleQuestionLike(
                                submittedQuestion.uid,
                                submittedQuestion.submittedByUid,
                              )
                            }}
                            disabled={isLikePending}
                            className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
                              liked
                                ? 'bg-blue text-white'
                                : 'bg-border-light text-text-secondary hover:bg-primary/10 hover:text-primary'
                            } disabled:cursor-not-allowed disabled:opacity-60`}
                          >
                            <FiThumbsUp className="h-4 w-4" />
                            {submittedQuestion.likesCount}
                          </button>
                        </div>
                      </article>
                    )
                  })}
                </div>
              )}
            </div>
          </section>
        ) : null}

        {!isUpcoming ? (
          <>
            <hr className="mt-10 border-border-light" />
            <CommentsSection
              targetId={webinar.uid}
              commentsOn={CommentsOn.Webinar}
              onAuthRequired={openLoginModal}
            />
          </>
        ) : null}

      </div>
      <NewsShareModal
        isOpen={isShareModalOpen}
        onClose={() => {
          setIsShareModalOpen(false)
        }}
        shareUrl={shareUrl}
        title={webinar.title}
        description={webinarDescription}
        imageUrl={shareImageUrl}
      />
    </article>
  )
}
